/**
 * MenuPDFGenerator — generates a print-ready PDF of the restaurant menu.
 * Fetches live data from the public API so the PDF always reflects the current menu.
 * Opens a styled print window with dark luxury layout (A4, multi-page).
 */
import { useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PUBLIC_API, PUBLIC_BACKEND_BASE } from "@/hooks/usePublicMenu";

function absUrl(url) {
  if (!url || typeof url !== "string" || !url.trim()) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${PUBLIC_BACKEND_BASE}${url}`;
  return url;
}

function fmtCurrency(amount, currency = "EUR") {
  const symbols = { EUR: "€", USD: "$", BRL: "R$", GBP: "£", AED: "AED " };
  const sym = symbols[currency] || currency + " ";
  const val = (typeof amount === "number" ? amount : 0).toFixed(2).replace(".", ",");
  return ["BRL", "USD", "GBP"].includes(currency) ? `${sym}${val}` : `${val}${sym}`;
}

function fmtHours(businessHours) {
  const dayMap = { domingo: "Dom", lunes: "Lun", martes: "Mar", miercoles: "Mié", jueves: "Jue", viernes: "Vie", sabado: "Sáb" };
  return (businessHours || [])
    .filter(h => h.enabled && h.slots?.length)
    .map(h => `${dayMap[h.day] || h.day}: ${(h.slots || []).map(s => s.join("–")).join(", ")}`)
    .join(" | ");
}

function buildHTML({ restaurant, categories, products, theme, menuUrl, qrDataUrl }) {
  const logoUrl = absUrl(theme?.logo_url || "");
  const primary = theme?.menu_colors?.primary || "#CDA34F";
  const name = restaurant?.name || "Restaurante";
  const phone = (restaurant?.phone || "").replace(/[^0-9+]/g, "");
  const address = restaurant?.address || "";
  const currency = restaurant?.currency || "EUR";
  const hoursLine = fmtHours(restaurant?.business_hours);
  const year = new Date().getFullYear();

  const productMap = {};
  for (const p of products) productMap[p.id || p._id] = p;

  const catSections = categories
    .map(cat => {
      const items = products.filter(p => p.category_id === cat.id && p.available !== false);
      if (!items.length) return "";
      const rows = items.map(p => {
        const img = absUrl(p.image_url || "");
        const allergens = (p.allergens || []).join(", ");
        return `
        <div class="product-row">
          ${img ? `<img class="product-img" src="${img}" alt="" onerror="this.style.display='none'" />` : '<div class="product-img-placeholder"></div>'}
          <div class="product-info">
            <div class="product-top">
              <span class="product-name">${p.name || ""}</span>
              <span class="product-price">${fmtCurrency(p.price, currency)}</span>
            </div>
            ${p.description ? `<p class="product-desc">${p.description}</p>` : ""}
            ${allergens ? `<p class="product-allergens">⚠ ${allergens}</p>` : ""}
          </div>
        </div>`;
      }).join("");
      return `
      <div class="category-section">
        <div class="category-header">
          <span class="category-name">${cat.name}</span>
        </div>
        ${rows}
      </div>`;
    })
    .join("");

  const styles = `
    @page { size: A4; margin: 14mm 12mm 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      background: #0a0a0a;
      color: #f3f4f6;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.5;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    /* COVER */
    .cover {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      min-height: 260mm; page-break-after: always; background: #0a0a0a;
      text-align: center; gap: 0;
    }
    .cover-logo { width: 110px; height: 110px; border-radius: 50%; object-fit: contain; border: 3px solid ${primary}; margin-bottom: 20px; }
    .cover-logo-placeholder { width: 110px; height: 110px; border-radius: 50%; border: 3px solid ${primary}; margin-bottom: 20px; }
    .cover-title { font-size: 30px; font-weight: 900; color: ${primary}; text-transform: uppercase; letter-spacing: 5px; margin-bottom: 8px; }
    .cover-subtitle { font-size: 12px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 3px; margin-bottom: 32px; }
    .cover-divider { width: 60px; height: 2px; background: ${primary}; margin: 0 auto 32px; }
    .cover-qr { background: #fff; padding: 10px; border-radius: 8px; margin-bottom: 16px; display: inline-block; }
    .cover-qr img { width: 120px; height: 120px; display: block; }
    .cover-qr-label { font-size: 10px; color: rgba(255,255,255,0.5); letter-spacing: 2px; text-transform: uppercase; margin-bottom: 28px; }
    .cover-info { font-size: 11px; color: rgba(255,255,255,0.6); margin-bottom: 6px; }
    .cover-info strong { color: ${primary}; }
    .cover-url { font-size: 10px; color: rgba(255,255,255,0.3); margin-top: 10px; letter-spacing: 1px; }
    .cover-footer { margin-top: 32px; font-size: 9px; color: rgba(255,255,255,0.2); text-transform: uppercase; letter-spacing: 2px; }
    /* CONTENT PAGES */
    .page-header {
      display: flex; align-items: center; justify-content: space-between;
      padding-bottom: 8px; margin-bottom: 16px;
      border-bottom: 1px solid rgba(205,163,79,0.3);
    }
    .page-header-name { font-size: 13px; font-weight: 800; color: ${primary}; text-transform: uppercase; letter-spacing: 2px; }
    .page-header-tag { font-size: 9px; color: rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 1px; }
    /* CATEGORY */
    .category-section { margin-bottom: 18px; page-break-inside: avoid; }
    .category-header {
      background: ${primary}; border-radius: 4px;
      padding: 5px 10px; margin-bottom: 8px;
    }
    .category-name { font-size: 12px; font-weight: 800; color: #0a0a0a; text-transform: uppercase; letter-spacing: 2px; }
    /* PRODUCT */
    .product-row {
      display: flex; gap: 10px; padding: 8px 0;
      border-bottom: 1px solid rgba(255,255,255,0.07);
      page-break-inside: avoid;
    }
    .product-img { width: 58px; height: 58px; object-fit: cover; border-radius: 6px; flex-shrink: 0; }
    .product-img-placeholder { width: 58px; height: 58px; border-radius: 6px; flex-shrink: 0; background: rgba(255,255,255,0.04); }
    .product-info { flex: 1; min-width: 0; }
    .product-top { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 3px; }
    .product-name { font-size: 12px; font-weight: 700; color: #f3f4f6; }
    .product-price { font-size: 13px; font-weight: 900; color: ${primary}; flex-shrink: 0; margin-left: 8px; }
    .product-desc { font-size: 10px; color: rgba(255,255,255,0.5); line-height: 1.4; margin-bottom: 3px; }
    .product-allergens { font-size: 9px; color: #f59e0b; }
    /* FOOTER */
    @media print {
      body { background: #0a0a0a !important; }
    }
  `;

  const coverQRBlock = qrDataUrl
    ? `<div class="cover-qr"><img src="${qrDataUrl}" width="120" height="120" /></div><p class="cover-qr-label">Escanea para pedir online</p>`
    : "";

  const menuSection = catSections ? `
    <div class="page-header">
      <span class="page-header-name">${name}</span>
      <span class="page-header-tag">Menú ${year}</span>
    </div>
    ${catSections}` : "";

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Menú ${name}</title>
  <style>${styles}</style>
</head>
<body>
  <div class="cover">
    ${logoUrl ? `<img class="cover-logo" src="${logoUrl}" alt="${name}" onerror="this.style.display='none'" />` : '<div class="cover-logo-placeholder"></div>'}
    <h1 class="cover-title">${name}</h1>
    <p class="cover-subtitle">Menú ${year}</p>
    <div class="cover-divider"></div>
    ${coverQRBlock}
    ${phone ? `<p class="cover-info">📱 WhatsApp: <strong>+${phone.replace(/^\+/, "")}</strong></p>` : ""}
    ${address ? `<p class="cover-info">📍 ${address}</p>` : ""}
    ${hoursLine ? `<p class="cover-info" style="margin-top:6px;font-size:10px;color:rgba(255,255,255,0.4)">${hoursLine}</p>` : ""}
    ${menuUrl ? `<p class="cover-url">${menuUrl}</p>` : ""}
    <p class="cover-footer">Kaiso System · Córdoba · ${year}</p>
  </div>
  ${menuSection}
</body>
</html>`;
}

export function MenuPDFGenerator({ menuUrl, menuSlug, restaurant }) {
  const [loading, setLoading] = useState(false);
  const qrRef = useRef(null);

  async function handleGenerate() {
    if (!menuSlug) {
      toast.error("Configure o slug do restaurante primeiro");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${PUBLIC_API}/public/menu/${menuSlug}`, { timeout: 15000 });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const canvas = qrRef.current?.querySelector("canvas");
      const qrDataUrl = canvas ? canvas.toDataURL("image/png") : "";

      const html = buildHTML({
        restaurant: data.restaurant || restaurant,
        categories: data.categories || [],
        products: data.products || [],
        theme: data.theme || {},
        menuUrl,
        qrDataUrl,
      });

      const win = window.open("", "_blank", "width=900,height=700,scrollbars=yes");
      if (!win) {
        toast.error("Bloqueio de popup. Permita popups para gerar o PDF.");
        setLoading(false);
        return;
      }
      win.document.write(html);
      win.document.close();
      win.focus();
      // Wait for images to load before triggering print
      setTimeout(() => { win.print(); }, 1500);
    } catch (err) {
      toast.error("Erro ao gerar PDF: " + (err.message || "Tente novamente"));
    }
    setLoading(false);
  }

  return (
    <>
      {/* Hidden QR canvas used to embed QR into the PDF cover */}
      <div ref={qrRef} style={{ position: "fixed", left: "-9999px", top: "-9999px", visibility: "hidden" }}>
        <QRCodeCanvas value={menuUrl || "https://kaisosystem.com"} size={256} level="H" />
      </div>
      <Button
        onClick={handleGenerate}
        disabled={loading || !menuSlug}
        variant="outline"
        size="sm"
        className="gap-1.5"
        data-testid="download-menu-pdf-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
        {loading ? "Gerando PDF..." : "Baixar Menu PDF"}
      </Button>
    </>
  );
}
